import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, Card, Input } from '@bruno/ui';
import type { PublicCaptchaConfigDTO } from '@bruno/shared';
import { useAuth } from '../auth/AuthContext';
import { useCapCaptcha } from '../auth/useCapCaptcha';
import { getCaptchaConfig } from '../api/auth';
import { forgotPassword } from '../api/account';
import { CaptchaStatusBadge } from '../components/CaptchaStatusBadge';

export function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaCfg, setCaptchaCfg] = useState<PublicCaptchaConfigDTO | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getCaptchaConfig()
      .then((cfg) => {
        if (!cancelled) setCaptchaCfg(cfg);
      })
      .catch(() => {
        if (!cancelled) setCaptchaCfg({ enabled: false, instanceUrl: '', siteKey: '' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) {
        throw new Error('Captcha verification failed. Please try again.');
      }
      await forgotPassword({ email, captchaToken: token ?? undefined });
      setDone(true);
    } catch (err) {
      if (captchaCfg?.enabled) captcha.reset();
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Card className="w-full max-w-md">
        <h1 className="text-xl font-semibold">Forgot your password?</h1>
        {done ? (
          // Intentionally generic so the existence of an account
          // cannot be inferred from the response.
          <>
            <p className="text-sm text-slate-600 mt-2">
              If an account exists for that email, a reset link is on its way. The link
              expires in 30 minutes — check your spam folder if it doesn't arrive shortly.
            </p>
            <div className="mt-6">
              <Link to="/login" className="text-sm text-indigo-600 hover:underline">
                ← Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-500 mt-1">
              Enter your email and we'll send you a link to reset your password.
            </p>
            <form onSubmit={onSubmit} className="flex flex-col gap-4 mt-5">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              {error ? <div className="text-sm text-red-600">{error}</div> : null}
              <Button type="submit" loading={submitting}>
                Send reset link
              </Button>
              {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
            </form>
            <div className="mt-4 text-center text-sm text-slate-500">
              Remembered it?{' '}
              <Link to="/login" className="text-indigo-600 hover:underline">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
